/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Ryan Duffy
 */
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { NotificationService } from '../api-services/notifications/notification.service';
import * as uuid from '@base/node_modules/uuid';

import { ProjectsListService } from '../api-services/projects/projects-list.service';
import { TasksListService } from '../api-services/tasks/tasks-list.service';
import { NotificationUserService } from '../api-services/notification-users/notification-user.service';
import { ProjectModel } from '../../models/project.model';
import { TaskModel } from '../../models/task.model';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { NotesListService } from '@app/services/api-services/notes/notes-list.service';
import { NoteModel } from '../../models/note.model';
import { NotificationModel } from 'app/models/notification.model';
import { NotificationUserModel } from 'app/models/notification-user.model';

@Injectable({
  providedIn: 'root',
})
export class HttpNotificationsService implements OnDestroy {
  // Variables that will be set everytime this service is instantiated
  private endpoint: string;
  private oldData: any;
  private notificationId: string;
  private notificationTimestamp: Date;
  private notificationSourceId: string;
  private notificationSourceName: string;
  private notificationType: string;
  private notificationUser: string;
  private notificationOwner: string;

  // Subscriptions
  private notificationSubscription: Subscription;
  constructor(private injector: Injector) {}

  handleRequest(request: HttpRequest<any>) {
    const authService = this.injector.get(AuthenticationService);
    const acceptedEndpoints = [
      'task_users',
      'project_users',
      'projects',
      'tasks',
      'notes',
      'note_users',
    ];
    if (
      // Ignore get requests
      request.method !== 'GET' &&
      // Only generate a notification for specific endpoints
      acceptedEndpoints.includes(request.headers.get('endpoint'))
    ) {
      this.endpoint = request.headers.get('endpoint');
      this.notificationId = uuid.v4();
      this.notificationTimestamp = new Date();
      this.oldData = request.headers.get('data') || null;
      this.notificationOwner = authService.user.userId;
      if (this.oldData) {
        // Ensure the current user doesn't provide themself with notifications
        if (
          this.oldData.user_id !== authService.user.userId &&
          this.oldData.owner_id !== authService.user.userId &&
          this.oldData.project_owner_id !== authService.user.userId
        ) {
          this.determineNotification(request);
        }
        // Ensure the current user doesn't provide themself with notifications
      } else if (
        request.body.user_id !== authService.user.userId &&
        request.body.owner_id !== authService.user.userId &&
        request.body.project_owner_id !== authService.user.userId
      ) {
        this.determineNotification(request);
      }
    }
  }
  /**
   *  determine the notifications to be created when an action requires this
   *
   * @param {HttpRequest<any>} request
   * @memberof HttpNotificationsService
   */
  determineNotification(request: HttpRequest<any>) {
    const notificationService = this.injector.get(NotificationService);
    const notificationUserService = this.injector.get(NotificationUserService);
    const projectListService = this.injector.get(ProjectsListService);
    const taskListService = this.injector.get(TasksListService);
    const noteListService = this.injector.get(NotesListService);
    const noProjectModel = new ProjectModel();
    const noTaskModel = new TaskModel();
    const noNoteModel = new NoteModel();
    let project: ProjectModel;
    let task: TaskModel;
    let note: NoteModel;
    noProjectModel.projectName = 'Project not found';
    noTaskModel.taskHeading = 'Task not found';
    noNoteModel.noteHeading = 'No Note Found';
    // For each endpoint, get different data to provide to the notification that will be generated i.e. project-analytics, task etc.
    switch (this.endpoint) {
      case 'project_users':
        // For each type of request, get different data to provide to the new notification record i.e. POST, PATCH, DELETE etc.
        switch (request.method) {
          case 'POST':
            this.notificationType = 'project_add';
            this.notificationSourceId = request.body.project_id;
            this.notificationUser = request.body.user_id;
            project =
              projectListService.getProjectsById(this.notificationSourceId) || noProjectModel;
            this.notificationSourceName = project.projectName;
            break;
          case 'DELETE':
            this.notificationType = 'project_remove';
            this.notificationSourceId = this.oldData.project_id;
            this.notificationUser = this.oldData.user_id;
            project =
              projectListService.getProjectsById(this.notificationSourceId) || noProjectModel;
            this.notificationSourceName = project.projectName;
        }
        break;
      case 'task_users':
        switch (request.method) {
          case 'POST':
            this.notificationType = 'task_add';
            this.notificationSourceId = request.body.task_id;
            this.notificationUser = request.body.user_id;
            task = taskListService.getTaskById(this.notificationSourceId) || noTaskModel;
            this.notificationSourceName = task.taskHeading;
            break;
          case 'DELETE':
            this.notificationType = 'task_remove';
            this.notificationSourceId = this.oldData.task_id;
            this.notificationUser = this.oldData.user_id;
            task = taskListService.getTaskById(this.notificationSourceId) || noTaskModel;
            this.notificationSourceName = task.taskHeading;
            break;
        }
        break;
      case 'note_users':
        switch (request.method) {
          case 'POST':
            this.notificationType = 'note_add';
            this.notificationSourceId = request.body.note_id;
            this.notificationUser = request.body.user_id;
            note = noteListService.getNoteByNoteId(this.notificationSourceId) || noNoteModel;
            this.notificationSourceName = note.noteHeading;
            break;
          case 'DELETE':
            this.notificationType = 'note_remove';
            this.notificationSourceId = this.oldData.note_id;
            this.notificationUser = this.oldData.user_id;
            note = noteListService.getNoteByNoteId(this.notificationSourceId) || noNoteModel;
            this.notificationSourceName = note.noteHeading;
            break;
        }
        break;
      case 'projects':
        switch (request.method) {
          case 'PATCH':
            this.notificationType = 'project_update';
            this.notificationSourceId = this.oldData.project_id;
            this.notificationUser = this.oldData.project_owner_id;
            this.notificationSourceName = this.oldData.project_name;
            project =
              projectListService.getProjectsById(this.notificationSourceId) || noProjectModel;
            this.notificationSourceName = project.projectName;
            break;
          case 'DELETE':
            this.notificationType = 'project_delete';
            this.notificationSourceId = this.oldData.project_id;
            this.notificationUser = this.oldData.project_owner_id;
            this.notificationSourceName = this.oldData.project_name;
            project =
              projectListService.getProjectsById(this.notificationSourceId) || noProjectModel;
            this.notificationSourceName = project.projectName;
            break;
        }
        break;
      case 'tasks':
        switch (request.method) {
          case 'PATCH':
            this.notificationType = 'task_update';
            this.notificationSourceId = this.oldData.task_id;
            this.notificationUser = this.oldData.owner_id;
            task = taskListService.getTaskById(this.notificationSourceId) || noTaskModel;
            this.notificationSourceName = task.taskHeading;
            break;
        }
        break;
    }
    if (this.notificationUser) {
      notificationService.createNotifications([{notification: new NotificationModel(
        this.notificationId,
        this.notificationType,
        this.notificationSourceId,
        this.notificationOwner,
        this.notificationTimestamp,
        false,
        this.notificationTimestamp,
        this.notificationSourceName
      ), notificationUser: new NotificationUserModel(
        this.notificationId,
        this.notificationUser
      )}])
        .subscribe(() => {
          if (this.notificationSubscription) {
            this.notificationSubscription.unsubscribe();
          }
        });
    }
  }

  ngOnDestroy() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }
}
